/** Ported from chief's `web/src/ConfirmDialog.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type ReactNode, useEffect, useRef } from 'react';

/**
 * A modal confirmation, on the native `<dialog>` element.
 *
 * `window.confirm` cannot render the facts that make a decision like this
 * safe — which content is about to be deleted, and what goes with it — as
 * anything but a wall of newline-joined text. `showModal()` gives
 * the focus trap, Esc, the inert background and focus restoration for free, so
 * this is a small component rather than a hand-rolled one.
 *
 * Cancel takes focus deliberately: a stray Enter must not delete anything.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  alternative,
  busyLabel = 'Working…',
  busy = false,
  confirmDisabled = false,
  danger = false,
  onConfirm,
  onCancel,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly children: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  /**
   * A third way out, between the two: "Discard" next to "Save" and "Cancel".
   * Never the one that takes focus.
   */
  readonly alternative?: { readonly label: string; readonly onClick: () => void };
  readonly busyLabel?: string;
  readonly busy?: boolean;
  /** The confirmation is not yet a valid one — e.g. a required reason is blank. */
  readonly confirmDisabled?: boolean;
  /** A destructive confirmation is red, so the eye checks before the hand. */
  readonly danger?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    // `showModal()` throws if it is already open, which StrictMode's double
    // invocation would otherwise trigger.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      className="dialog"
      ref={ref}
      // Esc and the backdrop both fire `cancel`; neither may count as consent.
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <h2 className="dialog__title">{title}</h2>
      <div className="dialog__body">{children}</div>
      <div className="dialog__actions">
        <button type="button" className="button" onClick={onCancel} disabled={busy} autoFocus>
          {cancelLabel}
        </button>
        {alternative !== undefined && (
          <button type="button" className="button" onClick={alternative.onClick} disabled={busy}>
            {alternative.label}
          </button>
        )}
        <button
          type="button"
          className={danger ? 'button button--danger-solid' : 'button button--primary'}
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
        >
          {busy ? busyLabel : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
